import { supabase } from "@/integrations/supabase/client";

export interface LinkedInDocData {
  LinkedIn_First_Name: string;
  LinkedIn_Last_Name: string;
  LinkedIn_Headline_1: string;
  LinkedIn_Headline_2: string;
  LinkedIn_About: string;
  LinkedIn_Experience: string;
  LinkedIn_URL: string;
}

export interface WordDocumentData {
  filename: string;
  data: string;
  size: number;
  publicUrl?: string;
}

/**
 * Parse GPT-generated LinkedIn content into structured JSON for Docmosis
 */
export const parseLinkedInContentForDocmosis = (
  content: string,
  targetJobTitle: string
): LinkedInDocData => {
  // Extract First Name / Last Name
  const firstNameMatch = content.match(/\*\*First Name:\*\*\s*(.+?)(?:\n|$)/i);
  const lastNameMatch = content.match(/\*\*Last Name:\*\*\s*(.+?)(?:\n|$)/i);

  // Extract Headlines - handle multiple formats
  const headline1Match = content.match(
    /\*\*Headline Option 1:\*\*\s*([\s\S]*?)(?:\n\n|\*\*Headline Option 2|$)/i
  );
  const headline2Match = content.match(
    /\*\*Headline Option 2:\*\*\s*([\s\S]*?)(?:\n\n|---|$)/i
  );

  // Extract About Section - handle multiple header formats
  const aboutPatterns = [
    /\*\*ABOUT SECTION:\*\*\s*([\s\S]*?)(?=\*\*PROFESSIONAL EXPERIENCE|$)/i,
    /\*\*About Section:\*\*\s*([\s\S]*?)(?=\*\*PROFESSIONAL EXPERIENCE|$)/i,
    /### ABOUT SECTION\s*([\s\S]*?)(?=###|$)/i,
  ];
  
  let aboutSection = '';
  for (const pattern of aboutPatterns) {
    const match = content.match(pattern);
    if (match && match[1]?.trim()) {
      aboutSection = match[1].trim();
      break;
    }
  }

  // Extract Experience Section - handle multiple header formats
  const expPatterns = [
    /\*\*PROFESSIONAL EXPERIENCE SECTION:\*\*\s*([\s\S]*?)(?=\*\*ADDITIONAL|\*\*SKILLS|$)/i,
    /\*\*PROFESSIONAL EXPERIENCE:\*\*\s*([\s\S]*?)(?=\*\*ADDITIONAL|\*\*SKILLS|$)/i,
    /### PROFESSIONAL EXPERIENCE\s*([\s\S]*?)(?=###|$)/i,
  ];
  
  let experienceSection = '';
  for (const pattern of expPatterns) {
    const match = content.match(pattern);
    if (match && match[1]?.trim()) {
      experienceSection = match[1].trim();
      break;
    }
  }

  return {
    LinkedIn_First_Name: firstNameMatch?.[1]?.trim() || '',
    LinkedIn_Last_Name: lastNameMatch?.[1]?.trim() || '',
    LinkedIn_Headline_1: headline1Match?.[1]?.trim() || targetJobTitle || '',
    LinkedIn_Headline_2: headline2Match?.[1]?.trim() || '',
    LinkedIn_About: aboutSection,
    LinkedIn_Experience: experienceSection,
    LinkedIn_URL: '',
  };
};

/**
 * Upload a base64 Word document to Supabase Storage
 */
export const uploadLinkedInDocToStorage = async (
  filename: string,
  base64Data: string
): Promise<string | undefined> => {
  try {
    // Convert base64 to blob
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    // Create safe filename
    const safeFileName = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
    const timestamp = Date.now();
    const storagePath = `linkedin_${timestamp}_${safeFileName}`;

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('word-documents')
      .upload(storagePath, blob, { cacheControl: '3600', upsert: false });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return undefined;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('word-documents')
      .getPublicUrl(storagePath);

    return urlData?.publicUrl;
  } catch (error) {
    console.error('Failed to upload to storage:', error);
    return undefined;
  }
};

/**
 * Generate LinkedIn Word document via Docmosis
 */
export const generateLinkedInWordDocument = async (
  generatedContent: string,
  targetJobTitle: string
): Promise<WordDocumentData | null> => {
  try {
    console.log('=== Generating LinkedIn Word Document ===');
    
    // Parse the content into structured JSON
    const linkedinDocData = parseLinkedInContentForDocmosis(
      generatedContent,
      targetJobTitle
    );
    
    console.log('Parsed LinkedIn data for Docmosis:', {
      hasFirstName: !!linkedinDocData.LinkedIn_First_Name,
      hasHeadline1: !!linkedinDocData.LinkedIn_Headline_1,
      aboutLength: linkedinDocData.LinkedIn_About.length,
      expLength: linkedinDocData.LinkedIn_Experience.length,
    });

    // Call the Docmosis edge function
    const { data: docData, error: docError } = await supabase.functions.invoke(
      'generate-linkedin-document',
      { body: linkedinDocData }
    );

    if (docError) {
      console.error('Docmosis edge function error:', docError);
      return null;
    }

    if (!docData?.success || !docData?.files?.length) {
      console.error('Docmosis returned no files:', docData);
      return null;
    }

    const file = docData.files[0];
    console.log('Docmosis document generated:', file.filename, file.size, 'bytes');

    // Upload to storage for Word Online Viewer
    const publicUrl = await uploadLinkedInDocToStorage(file.filename, file.data);
    console.log('Document uploaded to storage:', publicUrl ? 'success' : 'failed');

    return {
      filename: file.filename,
      data: file.data,
      size: file.size,
      publicUrl,
    };
  } catch (error) {
    console.error('Failed to generate LinkedIn Word document:', error);
    return null;
  }
};

/**
 * Download a Word document from base64 data
 */
export const downloadWordDocument = (base64Data: string, filename: string): void => {
  try {
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download Word document:', error);
  }
};
